import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ExecutiveSummaryTask {
  taskId: string;
  rcNumber: string;
  status: 'pending' | 'processing' | 'finalizing' | 'success' | 'failed';
  pdfUrl?: string;
  errorMessage?: string;
  createdAt: number;
  completedAt?: number;
  cityName?: string;
  cityNameUrl?: string;
  fulladdress?: string;
}

interface ExecutiveSummaryState {
  tasks: Record<string, ExecutiveSummaryTask>;
  activeTaskId: string | null;
  isDialogOpen: boolean;
  isGenerating: boolean;
  
  // Actions
  setDialogOpen: (isOpen: boolean) => void;
  startGeneration: (rcNumber: string, taskId: string, cityNameUrl?: string, cityName?: string, fulladdress?: string) => void;
  updateTaskStatus: (taskId: string, status: ExecutiveSummaryTask['status'], pdfUrl?: string, errorMessage?: string) => void;
  completeTask: (taskId: string, pdfUrl: string) => void;
  failTask: (taskId: string, errorMessage: string) => void;
  clearTask: (taskId: string) => void;
  clearAll: () => void;
  getTaskByRcNumber: (rcNumber: string) => ExecutiveSummaryTask | null;
  cleanupOldTasks: () => void;
}

const MAX_TASK_AGE = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export const useExecutiveSummaryStore = create<ExecutiveSummaryState>()(
  persist(
    (set, get) => ({
      tasks: {},
      activeTaskId: null,
      isDialogOpen: false,
      isGenerating: false,

      setDialogOpen: (isOpen) => {
        set({ isDialogOpen: isOpen });
        // If closing dialog, clear active task
        if (!isOpen) {
          set({ activeTaskId: null, isGenerating: false });
        }
      },

      startGeneration: (rcNumber, taskId, cityNameUrl, cityName, fulladdress) => {
        const newTask: ExecutiveSummaryTask = {
          taskId,
          rcNumber,
          cityNameUrl,
          cityName,
          fulladdress,
          status: 'processing',
          createdAt: Date.now(),
        };

        set((state) => ({
          tasks: {
            ...state.tasks,
            [taskId]: newTask,
          },
          activeTaskId: taskId,
          isGenerating: true,
          isDialogOpen: true,
        }));
      },

      updateTaskStatus: (taskId, status, pdfUrl, errorMessage) => {
        set((state) => {
          const task = state.tasks[taskId];
          if (!task) return state;

          return {
            tasks: {
              ...state.tasks,
              [taskId]: {
                ...task,
                status,
                pdfUrl,
                errorMessage,
                ...(status === 'success' || status === 'failed' ? { completedAt: Date.now() } : {}),
              },
            },
            isGenerating: status === 'processing',
          };
        });
      },

      completeTask: (taskId, pdfUrl) => {
        set((state) => {
          const task = state.tasks[taskId];
          if (!task) return state;

          return {
            tasks: {
              ...state.tasks,
              [taskId]: {
                ...task,
                status: 'success',
                pdfUrl,
                completedAt: Date.now(),
              },
            },
            isGenerating: false,
          };
        });
      },

      failTask: (taskId, errorMessage) => {
        set((state) => {
          const task = state.tasks[taskId];
          if (!task) return state;

          return {
            tasks: {
              ...state.tasks,
              [taskId]: {
                ...task,
                status: 'failed',
                errorMessage,
                completedAt: Date.now(),
              },
            },
            isGenerating: false,
          };
        });
      },

      clearTask: (taskId) => {
        set((state) => {
          const newTasks = { ...state.tasks };
          delete newTasks[taskId];

          return {
            tasks: newTasks,
            activeTaskId: state.activeTaskId === taskId ? null : state.activeTaskId,
          };
        });
      },

      clearAll: () => {
        set({
          tasks: {},
          activeTaskId: null,
          isDialogOpen: false,
          isGenerating: false,
        });
      },

      getTaskByRcNumber: (rcNumber) => {
        const state = get();
        const tasks = Object.values(state.tasks);
        // Find the most recent task for this RC number
        const task = tasks
          .filter((t) => t.rcNumber === rcNumber)
          .sort((a, b) => b.createdAt - a.createdAt)[0];
        
        return task || null;
      },

      cleanupOldTasks: () => {
        set((state) => {
          const now = Date.now();
          const newTasks: Record<string, ExecutiveSummaryTask> = {};

          Object.entries(state.tasks).forEach(([taskId, task]) => {
            // Keep tasks that are less than 24 hours old or still processing
            if (task.status === 'processing' || now - task.createdAt < MAX_TASK_AGE) {
              newTasks[taskId] = task;
            }
          });

          return { tasks: newTasks };
        });
      },
    }),
    {
      name: 'executive-summary-storage',
      partialize: (state) => ({
        tasks: state.tasks,
      }),
    }
  )
);

